// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import { UseFormReturn } from 'react-hook-form/dist/types/form';

import { AxiosError, isAxiosError } from 'axios';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { toast } from 'hooks/useToaster';
import { canonicalAutoVehicleType } from 'lib/autoTransport/autoVehicleTypes';
import {
  AutoTransportDetails,
  DATQuoteLocationType,
  DATQuoteTimeframe,
  ProfitType,
  QuickQuoteInputs,
  QuoteFormStop,
  QuoteMode,
  QuoteTypeInSource,
  SelectedQuoteType,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { parseLocation } from 'pages/QuoteView/Quoting/RequestQuickQuote/utils/location';
import { Email } from 'types/Email';
import { Stop, TransportType } from 'types/QuoteRequest';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { Quoting } from 'types/enums/Integrations';
import { GenericSuggestion } from 'types/suggestions/CoreSuggestions';
import { TruPayResponseMetadata } from 'types/trupayQuote';

import { resolveQuickQuoteSourceContext } from './resolveQuickQuoteSourceContext';

interface GetQuickQuoteProps {
  isMultiStopQuickQuoteEnabled: boolean;
  email: Maybe<Email>;
  threadId?: string;
  clickedSuggestion: Maybe<GenericSuggestion>;
  formValues: QuickQuoteInputs;
  formMethods: UseFormReturn<QuickQuoteInputs>;
  setQuoteNotConfidentHandler: React.Dispatch<React.SetStateAction<boolean>>;
  setProfit: React.Dispatch<React.SetStateAction<number>>;
  profitType: ProfitType;
  quoteMode: QuoteMode;
  // Bound Chrome tab URL for portal source context.
  tabURL?: string;
}

interface BuildQuickQuoteBodyProps {
  isMultiStopQuickQuoteEnabled: boolean;
  email: Maybe<Email>;
  threadId?: string;
  clickedSuggestion: Maybe<GenericSuggestion>;
  formValues: QuickQuoteInputs;
  quoteMode: QuoteMode;
}

export interface QuickQuoteBody {
  loadId: string;
  transportType: TransportType;
  subTransportType?: string;
  newStops: Array<Stop>; // TODO: For new multi-stop format, rename to `stops` after v.48.0
  pickupDate: Date;
  deliveryDate: Date;
  customerName: string;
  emailID?: number;
  threadID?: string;
  quoteRequestId?: number;
  selectedQuickQuoteId?: number;
  ltl?: QuickQuoteInputs['ltl'];
  quoteMode: QuoteMode;
  // same vocabulary as create_suggestion
  sourceCategory?: string;
  source?: string;
  sourceExternalID?: string;
  sourceURL?: string;
  sourceHost?: string;

  // Integration-specific fields
  kencoUrgency?: QuickQuoteInputs['kencoUrgency'];
  autoTransport?: AutoTransportDetails;
}

export interface QuickQuoteResponse {
  quoteRequestId: number;
  stops: QuickQuoteResponseStop[];
  selectedRateName: SelectedQuoteType;
  // HotShot and Box Truck are approximated with Flatbed and Van respectively
  // so we need to indicate the transport type for this data to the user
  inputtedTransportType: TransportType;
  // Granular equipment value (e.g. "STEP DECK") echoed back from the request. It is not a
  // member of the parent-only TransportType enum, so it is typed as string — matching the
  // subTransportType request field above and the sub-transport helper signatures.
  inputtedSubTransportType?: string;
  submittedTransportType: TransportType;
  configuration?: {
    lowConfidenceThreshold: number;
    mediumConfidenceThreshold: number;
    belowThresholdMessage: string;
    defaultPercentMargin: number;
    defaultFlatMargin: number;
    fscProvider: string;
    customerTableFscPerMile?: number;
  };
  quoteReplyDraftTemplate: QuoteReplyDraftTemplate;

  quotes: QuickQuoteResponseQuote[];
  quoteErrors?: QuickQuoteResponseQuoteError[];
  isZipCodeLookup: boolean;
}

export type QuickQuoteResponseStop = {
  order: number;
  city: string;
  state: string;
  zip: string;
  country: string;
  marketArea?: string;
};

export type QuickQuoteResponseQuote = {
  id: number;
  source: Quoting;
  type: QuoteTypeInSource;
  rates: QuoteRates;
  distance: number;
  /** When set (e.g. pcmiler), miles used for DAT per-mile math differ from quote.source. */
  distanceSource?: string;
  metadata?: QuoteMetadata;
};

export type QuickQuoteResponseQuoteError = {
  source: Quoting;
  error: string;
};

export type QuoteRates = {
  target: number;
  low: number;
  high: number;
  targetPerMile: number;
  lowPerMile: number;
  highPerMile: number;
};

export type RouteSegment = {
  order: number; // Leg order, 0-indexed
  startStopIndex: number; // Index reference in stops array
  endStopIndex: number; // Index reference in stops array
  startCityState: string; // City, State
  endCityState: string; // City, State
  distanceMiles: number; // Miles
  rates: Maybe<QuoteRates>;
};

interface QuoteReplyDraftTemplate {
  subject: string;
  body: string;
}

export type KencoFlashQuoteMetadata = {
  fuel_cost_per_mile?: number;
  fuel_rate_per_mile?: number;
  mileage?: number;
  average_margin_pct?: number;
  outbound_tender_rejection_percent?: Maybe<number>;
  current_capacity_condition?: Maybe<string>;
  directional_capacity_change?: Maybe<string>;
  calculated_transit_days?: number;
  billable_layover_days?: number;
};

export type SonarQuoteMetadata = {
  laneScore?: number;
  capacityIndicator?: string;
  confidenceScore?: number;
  current_capacity_condition?: Maybe<string>;
  directional_capacity_change?: Maybe<string>;
  coverage_difficulty_score?: Maybe<number>;
  coverage_guidance?: Maybe<string>;
  origin_rejection_percent?: Maybe<number>;
  destination_rejection_percent?: Maybe<number>;
};

export enum SuperDispatchInternalLaneContextStatus {
  Pending = 'pending',
  Ready = 'ready',
  Unavailable = 'unavailable',
}

export type SuperDispatchVehicle = {
  type: string;
  year?: number;
  make?: string;
  model?: string;
  isInoperable?: boolean;
  requiresEnclosedTrailer?: boolean;
};

export type SuperDispatchLaneRecord = {
  id?: string;
  price: number;
  pricePerMile?: number;
  status?: string;
  pickupCity: string;
  pickupState: string;
  deliveryCity: string;
  deliveryState: string;
  distanceMiles?: number;
  postingDate?: string;
  availableDate?: string;
  dispatchedDate?: string;
  deliveredDate?: string;
  vehicles: SuperDispatchVehicle[];
  externalUrl?: string;
};

export type SuperDispatchTrendPoint = {
  date: string;
  price: number;
  minPrice?: number;
  maxPrice?: number;
};

export type SuperDispatchVehicleTypePriceTrend = {
  vehicleType: string;
  points: SuperDispatchTrendPoint[];
};

export type SuperDispatchPriceTrend =
  SuperDispatchVehicleTypePriceTrend[] | SuperDispatchTrendPoint[];

export type SuperDispatchQuoteMetadata = {
  confidence?: number;
  volume?: number;
  recentMoves?: Maybe<SuperDispatchLaneRecord[]>;
  recentPostings?: Maybe<SuperDispatchLaneRecord[]>;
  priceTrend?: Maybe<SuperDispatchPriceTrend>;
  externalUrl?: string;
  internalLaneContextStatus?: SuperDispatchInternalLaneContextStatus;
};

export type SuperDispatchInternalLaneContextResponse = {
  status: SuperDispatchInternalLaneContextStatus;
  recentMoves?: Maybe<SuperDispatchLaneRecord[]>;
  recentPostings?: Maybe<SuperDispatchLaneRecord[]>;
  priceTrend?: Maybe<SuperDispatchPriceTrend>;
  externalUrl?: string;
};

export type KitCoachQuoteSourceMetadata = {
  label: string;
  buy: number;
  low: number;
  high: number;
  confidenceLevel?: Undef<number>;
  isLowConfidence?: Undef<boolean>;
};

export type KitCoachCalculationStepMetadata = {
  id: string;
  title: string;
  description: string;
};

export type KitCoachQuoteMetadata = {
  transportType?: Undef<string>;
  targetBuy?: Undef<number>;
  buyLow?: Undef<number>;
  buyHigh?: Undef<number>;
  suggestedMargin?: Undef<number>;
  suggestedMarginType?: Undef<string>;
  marginBasis?: Undef<string>;
  distanceMiles?: Undef<number>;
  spreadPercent?: Undef<number>;
  sources?: Undef<KitCoachQuoteSourceMetadata[]>;
  isLowConfidence?: Undef<boolean>;
  summary?: Undef<string>;
  calculationSteps?: Undef<KitCoachCalculationStepMetadata[]>;
};

export type QuoteMetadata = {
  error?: string;

  // GS Metadata
  confidenceLevel?: number;

  // Kit Coach payload
  kitCoach?: KitCoachQuoteMetadata;

  // Super Dispatch Pricing Insights
  superDispatch?: SuperDispatchQuoteMetadata;

  // DAT Metadata
  companies?: number;
  reports?: number;
  timeframe?: DATQuoteTimeframe;
  originName?: string;
  originType?: DATQuoteLocationType;
  destinationName?: string;
  destinationType?: DATQuoteLocationType;
  fuelSurchargePerMile?: number;
  datMileage?: number;
  pcmilerMileage?: number;
  // For DAT longest leg multi-stop quotes, we use a flat fee for each intermediate stop
  stopFeeUSD?: number;
  stopFeeUSDLow?: number;
  stopFeeUSDMedium?: number;
  stopFeeUSDHigh?: number;

  // For multi-stop
  legs?: Maybe<RouteSegment[]>;

  intermediateStopsInRequest?: number;
} & Partial<KencoFlashQuoteMetadata> &
  Partial<SonarQuoteMetadata> &
  TruPayResponseMetadata;

type ErrorData = {
  error: string;
  threshold: Maybe<number>;
  defaultPercentMargin: number;
  defaultFlatMargin: number;
};

interface ValidationError {
  type: 'validation_error';
  message: string;
  details: StopValidationError[];
}

interface StopValidationError {
  stopIndex: number;
  field: string;
  type: string;
  message: string;
  value: string;
}

const defaultErrorMessage =
  'Unable to retrieve quote. Contact our team if the issue persists.';

export async function getQuickQuote({
  isMultiStopQuickQuoteEnabled,
  email,
  threadId,
  clickedSuggestion,
  formValues,
  formMethods,
  setQuoteNotConfidentHandler,
  setProfit,
  profitType,
  quoteMode = QuoteMode.TL,
  tabURL,
}: GetQuickQuoteProps): Promise<MaybeUndef<QuickQuoteResponse>> {
  setQuoteNotConfidentHandler(false);

  const sourceContext = resolveQuickQuoteSourceContext({
    tabURL,
    hasEmail: Boolean(email?.id),
  });

  // Source context only belongs on the quote request this call creates, so it is layered on here
  // instead of in buildQuickQuoteBody (shared with the lane-context poll).
  const body: QuickQuoteBody = {
    ...buildQuickQuoteBody({
      isMultiStopQuickQuoteEnabled,
      email,
      threadId,
      clickedSuggestion,
      formValues,
      quoteMode,
    }),
    ...(sourceContext && { ...sourceContext }),
  };

  try {
    const { data } = await axios.post<QuickQuoteResponse>(
      `/quote/quickquote/v2`,
      body
    );

    // Check for non-omitted errors from below-threshold confidence GS quotes
    const lowConfGSQuotes = data.quotes.filter(
      (quote: QuickQuoteResponseQuote) =>
        quote.metadata?.error &&
        quote.metadata?.error.includes('unable') &&
        quote.metadata?.error.includes('confident')
    );

    if (lowConfGSQuotes.length) {
      setProfit(
        profitType === ProfitType.Amount
          ? data.configuration?.defaultFlatMargin || 100
          : data.configuration?.defaultPercentMargin || 10
      );
      toast({
        description: `Greenscreens quote confidence is below
                ${
                  data.configuration?.lowConfidenceThreshold
                    ? data.configuration?.lowConfidenceThreshold + '%.'
                    : 'your minimum threshold.'
                }
                Try another quoting approach, then return to complete the form.`,
        variant: 'destructive',
      });

      data.quotes = data.quotes.filter(
        (quote: QuickQuoteResponseQuote) => !lowConfGSQuotes.includes(quote)
      );
      data.quotes.length === 0 && setQuoteNotConfidentHandler(true);
    }

    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      const status = error.response?.status;
      const data = error.response?.data;

      switch (status) {
        case 503: {
          setQuickQuoteFormError(error, formMethods);
          return;
        }
        case 502:
        case 504: {
          // Bad Gateway (502) or Gateway Timeout (504) - Greenscreens or other service is down/slow
          formMethods.setError('root', {
            message:
              'One or more quoting services are temporarily unavailable. Please try again or contact our team if the issue persists.',
          });
          return;
        }
        case 400: {
          setQuickQuoteFormError(error, formMethods);
          return;
        }
        default: {
          const errorMessage =
            typeof data === 'string' ? data : data?.error || data?.message;

          formMethods.setError('root', {
            message: errorMessage || defaultErrorMessage,
          });
          return;
        }
      }
    }

    // Non-Axios error fallback
    formMethods.setError('root', {
      message: defaultErrorMessage,
    });
  }
  return null;
}

// The server can hold each request for about 15s while it refreshes lane data.
export const SUPER_DISPATCH_INTERNAL_LANE_CONTEXT_TIMEOUT_MS = 20_000;

export async function getSuperDispatchInternalLaneContext({
  isMultiStopQuickQuoteEnabled,
  email,
  threadId,
  clickedSuggestion,
  formValues,
  quoteMode = QuoteMode.TL,
  quoteRequestId,
  signal,
}: BuildQuickQuoteBodyProps & {
  quoteRequestId: number;
  signal?: AbortSignal;
}): Promise<SuperDispatchInternalLaneContextResponse> {
  const body = {
    ...buildQuickQuoteBody({
      isMultiStopQuickQuoteEnabled,
      email,
      threadId,
      clickedSuggestion,
      formValues,
      quoteMode,
    }),
    quoteRequestId,
  };

  const { data } = await axios.post<SuperDispatchInternalLaneContextResponse>(
    '/quote/quickquote/superdispatch/lane-context',
    body,
    {
      signal,
      timeout: SUPER_DISPATCH_INTERNAL_LANE_CONTEXT_TIMEOUT_MS,
    }
  );

  return data;
}

export function buildQuickQuoteBody({
  isMultiStopQuickQuoteEnabled,
  email,
  threadId,
  clickedSuggestion,
  formValues,
  quoteMode,
}: BuildQuickQuoteBodyProps): QuickQuoteBody {
  const mappedStops = mapQuoteFormStopsToStops(
    formValues.stops,
    formValues.pickupDate,
    formValues.deliveryDate
  );

  const isAutoTransport = formValues.transportType === TransportType.AUTO;

  return {
    ...formValues,
    ...(email && { emailID: email.id }),
    ...((threadId || email?.threadID) && {
      threadID: threadId || email?.threadID,
    }),
    ...(clickedSuggestion && { quoteRequestId: clickedSuggestion.id }),
    loadId: formValues.loadId || '',
    newStops: mappedStops,
    // Don't send legacy 'stops' field if multi-stop is enabled so API can perform backwards-compatibility check
    // TODO: Deprecate after v.0.48.0
    stops: isMultiStopQuickQuoteEnabled ? undefined : formValues.stops,
    // Sends "TL" or "LTL". TODO(deprecate-FTL): older sidebar builds send "FTL"; the backend
    // accepts both, so it must deploy before this build ships.
    quoteMode,
    autoTransport: isAutoTransport
      ? toAutoTransportPayload(formValues.autoTransport)
      : undefined,
    subTransportType: isAutoTransport ? undefined : formValues.subTransportType,
  } as QuickQuoteBody;
}

function toAutoTransportPayload(
  details: Undef<AutoTransportDetails>
): Undef<AutoTransportDetails> {
  if (!details) {
    return undefined;
  }

  return {
    trailerType: details.trailerType,
    vehicles: details.vehicles.map((vehicle) => {
      const canonicalType = canonicalAutoVehicleType(vehicle.type);
      return {
        ...vehicle,
        year: vehicle.year.trim(),
        type: canonicalType ?? vehicle.type.trim(),
      };
    }),
  };
}

const setQuickQuoteFormError = (
  error: AxiosError<any, any>,
  formMethods: UseFormReturn<QuickQuoteInputs>
) => {
  const data = error.response?.data;

  // Handle structured validation errors for multi-stop loads
  if (data?.type === 'validation_error') {
    const validationError: ValidationError = data;

    // Check if there was a transient error while validating locations
    const isNonValidationError = validationError.details.some(
      (detail) => getValidationErrorMessage(detail.message) === ''
    );

    if (isNonValidationError) {
      toast({
        description: defaultErrorMessage,
        variant: 'destructive',
      });
      return;
    }

    // Set each validation error in the form
    validationError.details.forEach((detail) => {
      const fieldPath = `stops.${detail.stopIndex}.location`;
      const userMessage = getValidationErrorMessage(
        detail.message,
        formMethods.getValues(fieldPath)
      );

      // Set field-specific error
      formMethods.setError(fieldPath as `stops.${number}.location`, {
        type: 'warning',
        message: userMessage,
      });
    });

    // Set root error with summary
    formMethods.setError('root' as any, {
      message: 'One or more locations are invalid. Please check your input.',
    });

    return;
  }

  // Handle legacy string-based errors for 2-stop loads (for backwards compatibility with 2-stop)
  const legacyData: ErrorData = data;

  const isPickupError =
    legacyData.error.includes('stop 0') || legacyData.error.includes('stops.0');
  const isDropoffError =
    legacyData.error.includes('stop 1') || legacyData.error.includes('stops.1');

  let location: Undef<string>;
  if (isPickupError) {
    location = formMethods.getValues('stops.0.location');
  } else if (isDropoffError) {
    location = formMethods.getValues('stops.1.location');
  }

  const userMessage = getValidationErrorMessage(legacyData.error, location);
  if (!userMessage) {
    toast({
      description: defaultErrorMessage,
      variant: 'destructive',
    });
    return;
  }

  // Handle legacy 2-stop errors
  if (isPickupError) {
    formMethods.setError('stops.0.location' as any, {
      type: 'warning',
      message: userMessage,
    });
    formMethods.setError('root' as any, { message: userMessage });
  } else if (isDropoffError) {
    formMethods.setError('stops.1.location' as any, {
      type: 'warning',
      message: userMessage,
    });
    formMethods.setError('root' as any, { message: userMessage });
  } else {
    formMethods.setError('root' as any, { message: userMessage });
  }

  // Handle other errors
  toast({
    description: `Unable to retrieve quote. Please retry or contact our team if the issue persists.`,
    variant: 'destructive',
  });
};

// Map BE location-validation errors to user-facing copy.
export const getValidationErrorMessage = (
  errorString: string,
  location?: string
): string => {
  // Check for specific error patterns in the error string
  if (
    errorString.toLowerCase().includes('invalid zip code') || // USPS
    errorString.toLowerCase().includes('zip code not found')
  ) {
    return `The location doesn't exist. Please enter a valid zip code.`;
  }

  if (
    errorString.toLowerCase().includes('zipcode must be 5 characters') || // USPS
    errorString.toLowerCase().includes('is not 5 digits if usa') // USPS
  ) {
    return `Zip code must be 5 characters.`;
  }

  if (errorString.toLowerCase().includes('resolved outside requested state')) {
    const hasZip = Boolean(location && parseLocation(location)?.zip);
    if (hasZip) {
      return 'Failed to resolve location. Please check your input.';
    }

    return 'Failed to resolve location; try specifying a postal code.';
  }

  if (
    errorString.toLowerCase().includes('no zip code found for city/state') ||
    errorString.toLowerCase().includes('no valid zips found for city/state') ||
    errorString.toLowerCase().includes('city/state not found')
  ) {
    return `Couldn't find a postal code for this location. Try a nearby city or add a ZIP code.`;
  }

  if (
    errorString.toLowerCase().includes('error mapping city/state to zip') ||
    errorString.toLowerCase().includes('invalid location') ||
    errorString.toLowerCase().includes('location not found') ||
    errorString.toLowerCase().includes('no results')
  ) {
    return `The location could not be geolocated. Please check your input or try a ZIP code.`;
  }

  // If no specific pattern matches, return empty string
  return '';
};

// Helper function to map QuoteFormStop[] to Stop[]
export const mapQuoteFormStopsToStops = (
  quoteInputStops: QuoteFormStop[],
  pickupDate: Maybe<Date>,
  deliveryDate: Maybe<Date>
): Stop[] => {
  return quoteInputStops.map((stop, index) => {
    const isFirstStop = index === 0;
    const isLastStop = index === quoteInputStops.length - 1;

    const baseStop: Stop = {
      stopType: 'stop' as const,
      stopNumber: index,
      address: {
        city: stop.city,
        state: stop.state,
        zip: stop.zip,
        country: stop.country || 'US',
        name: '',
        addressLine1: '',
        addressLine2: '',
      },
    };

    if (isFirstStop) {
      return {
        ...baseStop,
        stopType: 'pickup' as const,
        readyTime: pickupDate,
      };
    } else if (isLastStop) {
      return {
        ...baseStop,
        stopType: 'dropoff' as const,
        mustDeliver: deliveryDate,
      };
    } else {
      return baseStop;
    }
  });
};
